<template>
  <div v-if="parsed || showError" class="ui-block">
    <UiBlockChoice
      v-if="parsed && parsed.type === 'choice'"
      :block="parsed"
      :interactive="interactive"
      :chosen-key="chosenKey"
      @action="onAction"
    />
    <UiBlockJobSearch
      v-else-if="parsed && parsed.type === 'job_search'"
      :block="parsed"
      :interactive="interactive"
      @action="onAction"
    />
    <UiBlockForm
      v-else-if="parsed && parsed.type === 'form'"
      :block="parsed"
      :interactive="interactive"
      @action="onAction"
    />
    <UiBlockDocList
      v-else-if="parsed && parsed.type === 'doc_list'"
      :block="parsed"
      :interactive="interactive"
      :summary-available="summaryAvailable"
      :open-available="openAvailable"
      :compare-available="compareAvailable"
      @action="onAction"
      @open-summary="(event, doc) => $emit('open-summary', event, doc)"
      @open-document="(doc) => $emit('open-document', doc)"
      @compare-documents="(docs) => $emit('compare-documents', docs)"
    />
    <UiBlockDocView
      v-else-if="parsed && parsed.type === 'doc_view'"
      :block="parsed"
      :interactive="interactive"
      :open-available="openAvailable"
      :summary-available="summaryAvailable"
      @action="onAction"
      @open-document="(doc) => $emit('open-document', doc)"
      @open-summary="(event, doc) => $emit('open-summary', event, doc)"
    />
    <UiBlockDocUpload
      v-else-if="parsed && parsed.type === 'doc_upload'"
      :block="parsed"
      :interactive="interactive"
      :open-available="openAvailable"
      :compare-available="compareAvailable"
      @action="onAction"
      @open-summary="(event, doc) => $emit('open-summary', event, doc)"
      @open-document="(doc) => $emit('open-document', doc)"
      @compare-documents="(docs) => $emit('compare-documents', docs)"
    />
    <div v-else-if="showError" class="ui-block__error">
      <v-icon size="14" color="grey">mdi-puzzle-outline</v-icon>
      This step couldn't be displayed.
    </div>
    <!--
      Parked-workflow hint: this is the newest workflow card in the
      thread, it was awaiting input, and a side conversation has
      superseded it (inert). The workflow itself is still parked
      server-side — saying "continue" re-renders the step as a fresh
      card at the bottom, so nobody has to interact up here.
    -->
    <div v-if="showParkedHint" class="ui-block__parked">
      <v-icon size="12" color="grey">mdi-pause-circle-outline</v-icon>
      This workflow is paused, not gone — use the Resume button by the message box (or just ask) when you're ready.
    </div>
    <!--
      Card footer: the optional feedback score widget next to the
      universal Exit / Finish links. The ENGINE stamps the feedback
      config only on END-POINT step cards (terminal, create-on-submit
      form, end-point doc_view/doc_upload) — the "last step" rule lives
      server-side, because a create-on-submit form is the last step
      while its card is still awaiting input. Rating wraps the flow up
      exactly like Exit/Finish.
    -->
    <div v-if="canExit || canFinish" class="ui-block__footer">
      <div v-if="feedbackConfig" class="ui-block__feedback">
        <div class="ui-block__feedback-label">
          {{ feedbackConfig.label }}
          <span v-if="feedbackError" class="ui-block__feedback-error">Couldn't save — try again</span>
          <!-- Client-configurable (atlas_feedback_saved_message); the
               fallback matches the engine's default for older payloads. -->
          <span v-else-if="feedbackSavedScore" class="ui-block__feedback-saved">✓ {{ feedbackConfig.saved_label || 'Saved. Your feedback is appreciated!' }}</span>
        </div>
        <!-- Clicking a score SAVES it immediately (the workflow stays
             where it is); re-clicking another score re-saves. -->
        <div class="ui-block__feedback-scale">
          <span v-if="feedbackConfig.pre_label" class="ui-block__feedback-cap">{{ feedbackConfig.pre_label }}</span>
          <button
            v-for="n in feedbackConfig.count"
            :key="n"
            type="button"
            class="ui-block__feedback-score"
            :class="{ 'ui-block__feedback-score--on': feedbackScore === n }"
            @click="pickScore(n)"
          >{{ n }}</button>
          <span v-if="feedbackConfig.post_label" class="ui-block__feedback-cap">{{ feedbackConfig.post_label }}</span>
        </div>
        <!-- Low score (within the configured trigger range): offer a
             written why. The comment saves ONLY via the button, which
             appears only while the text differs from what was last
             saved. -->

        <template v-if="feedbackWhyOpen">
          <div class="ui-block__feedback-label">{{ feedbackConfig.text_prompt || 'What could have been better?' }}</div>
          <!-- Save sits INSIDE the box and only while there is an unsaved
               edit: an always-visible (disabled) button read as an action
               the user still owed. Gone = nothing to save. -->
          <div class="ui-block__feedback-whyrow">
            <textarea
              v-model="feedbackWhy"
              rows="2"
              class="ui-field-input ui-block__feedback-why"
              :class="{ 'ui-block__feedback-why--editing': feedbackCommentDirty }"
            />
            <button
              v-if="feedbackCommentDirty"
              type="button"
              class="ui-primary-btn ui-block__feedback-send"
              @click="saveFeedbackComment()"
            >
              Save
            </button>
          </div>
          <!-- The Save button disappearing is not an acknowledgement:
               say so explicitly, and keep saying it for as long as what
               is in the box is what was stored. -->
          <div v-if="feedbackCommentSaved" class="ui-block__feedback-savednote">
            ✓ {{ feedbackConfig.comment_saved_label || 'Comment saved — thank you' }}
          </div>
        </template>
      </div>
      <!--
        Universal escape hatch / wrap-up. Clicking composes a
        [workflow:action] message with the reserved exit/finish action,
        which the engine accepts from any step.
      -->
      <!-- Engine-emitted "do it again" (Create another / Upload
           another): starts a fresh run parked on the step that produced
           the document, so repeat work skips the opening choice. -->
      <button
        v-if="repeatAction"
        type="button"
        class="ui-block__repeat"
        :title="`${repeatAction.label} without going back to the start`"
        @click="onRepeat"
      >
        <v-icon size="12" color="primary">mdi-plus-circle-outline</v-icon>
        {{ repeatAction.label }}
      </button>
      <button
        v-if="canExit && !carriesOwnExit"
        type="button"
        class="ui-block__exit"
        title="Leave this guided workflow"
        @click="onExit"
      >
        <v-icon size="12" color="grey">mdi-exit-to-app</v-icon>
        Exit workflow
      </button>
      <button
        v-else-if="canFinish"
        type="button"
        class="ui-block__exit"
        title="Wrap up this guided workflow"
        @click="onFinish"
      >
        <v-icon size="12" color="grey">mdi-check-circle-outline</v-icon>
        Finish workflow
      </button>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue';
import axios from 'axios';
import { parseUiBlock, buildWorkflowActionMessage } from '@rm-ui/agent/uiBlockSpec';
import UiBlockChoice from './UiBlockChoice.vue';
import UiBlockJobSearch from './UiBlockJobSearch.vue';
import UiBlockForm from './UiBlockForm.vue';
import UiBlockDocList from './UiBlockDocList.vue';
import UiBlockDocView from './UiBlockDocView.vue';
import UiBlockDocUpload from './UiBlockDocUpload.vue';

/**
 * Dispatches a raw `ui_block` (engine-authored, from
 * `ChatResponse.ui_blocks` / persisted history) to the component for
 * its type — the workflow analogue of ChartView's type dispatch.
 * Re-validates via parseUiBlock before mounting; malformed blocks get
 * a friendly placeholder instead of a broken card.
 *
 * Emits `action` with the composed workflow-action message text —
 * ChatMessage forwards it up the existing follow-up channel, so the
 * hosts (AtlasFlyout / vAgentChat) need no new wiring.
 */
export default defineComponent({
  name: 'UiBlockRenderer',
  components: { UiBlockChoice, UiBlockJobSearch, UiBlockForm, UiBlockDocList, UiBlockDocView, UiBlockDocUpload },
  props: {
    block: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    /** True when the host page can show the document-summary popup. */
    summaryAvailable: { type: Boolean, default: false },
    /** True when the host page can open the standard document view. */
    openAvailable: { type: Boolean, default: false },
    /** True when the host page can run the standard document compare. */
    compareAvailable: { type: Boolean, default: false },
    /** The surface's default workflow key (route atlas_context_workflow).
     * Sent as the exit action's value so the ENGINE restarts the default
     * workflow in the same call — deterministic bounce-to-menu. */
    exitWorkflowKey: { type: String, default: null },
    /** True when this message is the thread's newest workflow-card
     * message but no longer the latest assistant message — i.e. a side
     * conversation superseded a live card. Gates the parked hint. */
    parkedEligible: { type: Boolean, default: false },
    /** The instance the host's resume chip currently offers
     * (chat.parkedWorkflow), or null when nothing is resumable. The
     * hint must AGREE with the chip: a completed-in-place create form's
     * gate card stays emit-time 'awaiting_input' for ever, and telling
     * the user to "use the Resume button" while no button exists is
     * worse than no hint (screenshot report, 2026-08-27). */
    parkedInstanceId: { type: Number, default: null },
  },
  emits: ['action', 'open-summary', 'open-document', 'compare-documents'],
  setup(props, { emit }) {
    const parsed = computed(() => parseUiBlock(props.block));
    // Only surface the error card for blocks that CLAIM to be renderable
    // (have a type) — silently skip junk so history noise can't stack
    // placeholders.
    const showError = computed(() => !parsed.value && typeof props.block?.type === 'string');

    // The recorded answer for superseded choice cards. The engine keys
    // answers by step_key in instance state, but the display message
    // doesn't carry instance state — so we leave highlight-on-replay to
    // a later pass and only highlight within the live session via the
    // parent (null-safe here).
    const chosenKey = computed(() => props.block?.__chosen_key ?? null);

    function onAction(option) {
      if (!props.interactive || !parsed.value) return;
      const message = buildWorkflowActionMessage(parsed.value, option);
      if (message) emit('action', message, { block: parsed.value, option });
    }

    /**
     * Escape hatch shown only while the card is live: the block is the
     * latest interactive step AND the engine emitted it awaiting input
     * (end-point emits arrive 'completed' and need no exit).
     */
    const canExit = computed(
      () =>
        props.interactive &&
        parsed.value?.workflow?.status === 'awaiting_input',
    );

    /**
     * Parked hint: only on a card that was AWAITING INPUT when emitted
     * (end-point cards arrive 'completed' — nothing to resume there) and
     * only when the host says this is the newest workflow card yet not
     * the latest message. Emit-time status can be stale (a conversational
     * exit doesn't rewrite old cards), hence the soft wording.
     */
    const showParkedHint = computed(
      () =>
        props.parkedEligible &&
        !props.interactive &&
        parsed.value?.workflow?.status === 'awaiting_input' &&
        // Only while the resume chip actually offers THIS instance —
        // hint and chip are one affordance in two places.
        props.parkedInstanceId != null &&
        parsed.value?.workflow?.instance_id === props.parkedInstanceId,
    );

    /**
     * The card already offers the exit as one of its own buttons (the
     * create gate's "Exit workflow" sits beside "Create anyway"), so the
     * footer link would be a second identical control. The footer itself
     * still renders — it hosts the feedback widget.
     */
    const carriesOwnExit = computed(() => {
      const p = parsed.value?.props || {};
      return p.secondary_action?.action === 'exit' || p.footer_action?.action === 'exit';
    });

    /**
     * Wrap-up link on the latest card of a COMPLETED workflow (end-point
     * emits arrive status 'completed') — the finished sibling of exit:
     * the engine leaves the completed instance alone and restarts the
     * surface's default workflow.
     */
    const canFinish = computed(
      () =>
        props.interactive &&
        parsed.value?.workflow?.status === 'completed',
    );

    // ── Feedback score widget (workflow-level config) ──────────
    // Present only when the engine stamped the config on this block —
    // it does so exclusively for end-of-journey step cards, so
    // rendering whenever present IS the "last step only" rule.
    // Behaviour (user rules 2026-08-04): a score click SAVES immediately
    // and the workflow stays where it is; the save goes DIRECTLY to
    // advance_workflow over HTTP (no chat message — a chat-channel save
    // would post a new turn and de-activate the very card being rated).
    // A low score opens the written-why box; the comment saves only via
    // its button, which stays disabled until the text differs from what
    // was last saved. Each save overwrites the previous one server-side,
    // so score-only re-saves carry the last saved comment along rather
    // than wiping it.
    const feedbackConfig = computed(() => parsed.value?.workflow?.feedback ?? null);
    const feedbackScore = ref(null);
    const feedbackWhy = ref('');
    const feedbackWhyOpen = ref(false);
    const feedbackSavedScore = ref(null);
    const feedbackSavedComment = ref('');
    const feedbackError = ref(false);

    /** The comment in the box is exactly what was saved — the state the
     * confirmation line describes. Distinct from the score's own "Saved"
     * marker, because a rating without a comment saves nothing here. */
    const feedbackCommentSaved = computed(
      () =>
        !!feedbackSavedComment.value &&
        feedbackWhy.value.trim() === feedbackSavedComment.value,
    );

    /** There IS an unsaved comment edit — the only state in which Save
     * means anything, so it doubles as the button's visibility. */
    const feedbackCommentDirty = computed(() => {
      const comment = feedbackWhy.value.trim();
      return !!feedbackScore.value && !!comment && comment !== feedbackSavedComment.value;
    });

    async function saveFeedback(comment, onSaved) {
      const cfg = feedbackConfig.value;
      const wf = parsed.value?.workflow;
      const score = feedbackScore.value;
      if (!cfg || !wf || !score) return;
      feedbackError.value = false;
      try {
        await axios.post(
          '/agent/tools/advance_workflow/execute',
          {
            args: {
              instance_id: wf.instance_id,
              step_key: wf.step_key,
              action: 'feedback',
              value: { score, ...(comment ? { comment } : {}) },
            },
          },
          { headers: { 'X-Atlas-Surface': 'portal' } },
        );
        feedbackSavedScore.value = score;
        if (onSaved) onSaved();
      } catch (err) {
        feedbackError.value = true;
      }
    }

    function pickScore(n) {
      feedbackScore.value = n;
      const cfg = feedbackConfig.value;
      feedbackWhyOpen.value = !!(cfg && cfg.text_trigger_max && n <= cfg.text_trigger_max);
      // Same score again = nothing new to save.
      if (feedbackSavedScore.value === n) return;
      void saveFeedback(feedbackSavedComment.value, null);
    }

    function saveFeedbackComment() {
      if (!feedbackCommentDirty.value) return;
      const comment = feedbackWhy.value.trim();
      void saveFeedback(comment, () => {
        feedbackSavedComment.value = comment;
      });
    }

    function onExit() {
      onAction({
        key: 'exit',
        label: 'Exit workflow',
        // The default workflow key rides as the action value → the
        // engine cancels AND restarts the default in one advance.
        ...(props.exitWorkflowKey ? { value: props.exitWorkflowKey } : {}),
      });
    }

    /**
     * "Create another" / "Upload another" — the engine stamps the label
     * and which step to land on; the reserved `again` action carries the
     * step key as its value.
     */
    const repeatAction = computed(() =>
      props.interactive ? parsed.value?.workflow?.repeat : undefined,
    );

    function onRepeat() {
      const repeat = repeatAction.value;
      if (!repeat) return;
      onAction({ key: 'again', label: repeat.label, value: repeat.step_key });
    }

    function onFinish() {
      onAction({
        key: 'finish',
        label: 'Finish workflow',
        ...(props.exitWorkflowKey ? { value: props.exitWorkflowKey } : {}),
      });
    }

    return {
      parsed,
      showError,
      chosenKey,
      onAction,
      showParkedHint,
      canExit,
      carriesOwnExit,
      repeatAction,
      onRepeat,
      canFinish,
      onExit,
      onFinish,
      feedbackConfig,
      feedbackScore,
      feedbackWhy,
      feedbackWhyOpen,
      feedbackSavedScore,
      feedbackCommentDirty,
      feedbackCommentSaved,
      feedbackError,
      pickScore,
      saveFeedbackComment,
    };
  },
});
</script>

<style scoped>
.ui-block__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-top: 6px;
}
.ui-block__feedback {
  flex: 1 1 auto;
  min-width: 0;
}
.ui-block__feedback-label {
  font-size: 12px;
  color: rgb(55 65 81);
  margin-bottom: 4px;
}
.ui-block__feedback-scale {
  display: flex;
  align-items: center;
  gap: 4px;
}
.ui-block__feedback-cap {
  font-size: 11px;
  color: rgb(156 163 175);
  margin: 0 4px;
}
.ui-block__feedback-score {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid rgb(229 231 235);
  border-radius: 6px;
  background: white;
  font-family: inherit;
  font-size: 12px;
  color: rgb(55 65 81);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.ui-block__feedback-score:hover {
  border-color: rgb(165 180 252);
  background: rgb(238 242 255);
}
.ui-block__feedback-score--on {
  background: rgb(238 242 255);
  border-color: rgb(165 180 252);
  color: rgb(67 56 202);
}
/* The Save button overlays the textarea's bottom-right corner, so the
   comment box stays one control instead of a box-plus-button row. */
.ui-block__feedback-whyrow {
  position: relative;
  margin-top: 2px;
  max-width: 420px;
}
.ui-block__feedback-why {
  resize: vertical;
  min-height: 34px;
  width: 100%;
  box-sizing: border-box;
}
/* Only while editing: keeps typed text clear of the button, and avoids
   permanently reserving space for a button that isn't there. */
.ui-block__feedback-why--editing {
  padding-bottom: 30px;
}
.ui-block__feedback-send {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 3px 10px;
  font-size: 11px;
}
/* Sibling of the Exit/Finish links, but an ACTION rather than a way
   out — same size, carries the accent colour. */
.ui-block__repeat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-right: 10px;
  border: none;
  background: none;
  padding: 0;
  font-size: 11px;
  color: rgb(67 56 202);
  cursor: pointer;
}
.ui-block__repeat:hover {
  text-decoration: underline;
}
.ui-block__feedback-saved {
  margin-left: 6px;
  font-size: 11px;
  font-style: italic;
  color: rgb(5 150 105);
}
/* Comment confirmation: same positive language as the score's marker,
   but on its own line under the box where the Save button was. */
.ui-block__feedback-savednote {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 500;
  color: rgb(5 150 105);
}
.ui-block__feedback-error {
  margin-left: 6px;
  font-size: 11px;
  font-style: italic;
  color: rgb(185 28 28);
}
.ui-block__exit {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 11px;
  font-family: inherit;
  color: rgb(107 114 128);
  cursor: pointer;
}
.ui-block__exit:hover {
  color: rgb(55 65 81);
  text-decoration: underline;
}
.ui-block__error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px dashed rgb(209 213 219);
  color: rgb(107 114 128);
  font-size: 12px;
}
/* Muted footer line under a superseded-but-parked workflow card. */
.ui-block__parked {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 11px;
  font-style: italic;
  color: rgb(107 114 128);
}
</style>

<!-- Shared input/button look for the feedback widget — see uiBlockShared.css. -->
<style src="./uiBlockShared.css"></style>
